import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, Zap, Search, Filter, User, BadgeCheck, BookOpen } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { usePayment } from '@/context/PaymentContext';
import { useAuth } from '@/context/AuthContext';

interface TutorialStep {
  id: string;
  title: string;
  description: string;
  target: string;
  position: 'top' | 'bottom' | 'left' | 'right' | 'center';
  icon?: React.ReactNode;
}

const tutorialSteps: TutorialStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to RaiseBeacon!',
    description: 'Let\'s take a quick tour of the investor search tool. You can skip this anytime.',
    target: 'body',
    position: 'center',
    icon: <Zap className="w-5 h-5 text-yellow-500" />,
  },
  {
    id: 'search',
    title: 'Search Investors',
    description: 'Type here to search by name, firm, or keyword. Try "Sequoia" or "AI"!',
    target: '[data-tutorial="search"]',
    position: 'bottom',
    icon: <Search className="w-4 h-4 text-[#2B7FFF]" />,
  },
  {
    id: 'filters',
    title: 'Filter Results',
    description: 'Use these filters to narrow down by location, industry, stage, and investor type.',
    target: '[data-tutorial="filters"]',
    position: 'bottom',
    icon: <Filter className="w-4 h-4 text-[#2B7FFF]" />,
  },
  {
    id: 'results',
    title: 'Investor Cards',
    description: 'Click any card to view full details including contact info, portfolio, and more.',
    target: '[data-tutorial="investor-card"]',
    position: 'top',
    icon: <User className="w-4 h-4 text-[#2B7FFF]" />,
  },
  {
    id: 'navigation',
    title: 'Navigation',
    description: 'Use the navigation menu to switch between Home and Search. Login to unlock premium features!',
    target: '[data-tutorial="nav"]',
    position: 'bottom',
    icon: <BadgeCheck className="w-4 h-4 text-purple-500" />,
  },
  {
    id: 'complete',
    title: 'You\'re All Set!',
    description: 'Start exploring our curated investor network. Good luck with your fundraising!',
    target: 'body',
    position: 'center',
    icon: <Zap className="w-5 h-5 text-green-500" />,
  },
];

export function Tutorial() {
  const location = useLocation();
  const { isSubscribed: paymentSubscribed } = usePayment();
  const { isSubscribed: authSubscribed, isAdmin } = useAuth();
  const isSearchPage = location.pathname === '/search';
  
  // Must be subscribed via BOTH payment context AND auth (backend), OR be an admin
  const isSubscribed = (paymentSubscribed && authSubscribed) || isAdmin;
  
  const [isActive, setIsActive] = useState(false);
  const [showAskDialog, setShowAskDialog] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [hasSeenTutorial, setHasSeenTutorial] = useState(true);

  // Check if user has seen tutorial (for subscribed users or admins)
  useEffect(() => {
    if (!isSearchPage) {
      setIsActive(false);
      setShowAskDialog(false);
      return;
    }
    
    // Show tutorial for subscribed users OR admins
    if (!isSubscribed) {
      setIsActive(false);
      setShowAskDialog(false);
      setHasSeenTutorial(true); // Hide restart button too
      return;
    }
    
    const seen = localStorage.getItem('backr_tutorial_seen');
    if (!seen) {
      setHasSeenTutorial(false);
      // Show ask dialog instead of immediately starting tutorial
      setTimeout(() => setShowAskDialog(true), 500);
    }
  }, [isSearchPage, isSubscribed]);

  // Close when leaving search page
  useEffect(() => {
    if (!isSearchPage && isActive) {
      setIsActive(false);
    }
  }, [isSearchPage, isActive, isAdmin]);

  // Get target element position
  useEffect(() => {
    if (!isActive || !isSearchPage) return;

    const step = tutorialSteps[currentStep];
    if (step.target === 'body') {
      setTargetRect(null);
      return;
    }

    const updateTarget = () => {
      const element = document.querySelector(step.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect(rect);
        element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(updateTarget, 100);
    
    window.addEventListener('resize', updateTarget);
    window.addEventListener('scroll', updateTarget, true);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateTarget);
      window.removeEventListener('scroll', updateTarget, true);
    };
  }, [isActive, currentStep, isSearchPage]);

  const handleNext = useCallback(() => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      completeTutorial();
    }
  }, [currentStep]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const handleSkip = useCallback(() => {
    completeTutorial();
  }, []);

  const completeTutorial = () => {
    localStorage.setItem('backr_tutorial_seen', 'true');
    setHasSeenTutorial(true);
    setIsActive(false);
  };

  const startTutorial = () => {
    setShowAskDialog(false);
    setIsActive(true);
  };

  const skipTutorial = () => {
    localStorage.setItem('backr_tutorial_seen', 'true');
    setShowAskDialog(false);
    setHasSeenTutorial(true);
  };

  const resetTutorial = () => {
    localStorage.removeItem('backr_tutorial_seen');
    setHasSeenTutorial(false);
    setCurrentStep(0);
    setIsActive(true);
  };

  const step = tutorialSteps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === tutorialSteps.length - 1;

  // Calculate tooltip position
  const getTooltipStyles = () => {
    if (!targetRect || step.position === 'center') {
      return {
        position: 'fixed' as const,
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
      };
    }

    const spacing = 12;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let left = 0;
    let top = 0;

    switch (step.position) {
      case 'top':
        left = targetRect.left + targetRect.width / 2;
        top = targetRect.top - spacing;
        break;
      case 'bottom':
        left = targetRect.left + targetRect.width / 2;
        top = targetRect.bottom + spacing;
        break;
      case 'left':
        left = targetRect.left - spacing;
        top = targetRect.top + targetRect.height / 2;
        break;
      case 'right':
        left = targetRect.right + spacing;
        top = targetRect.top + targetRect.height / 2;
        break;
      default:
        left = viewportWidth / 2;
        top = viewportHeight / 2;
    }

    // Clamp to viewport
    left = Math.max(150, Math.min(viewportWidth - 150, left));
    top = Math.max(100, Math.min(viewportHeight - 150, top));

    return {
      position: 'fixed' as const,
      left: `${left}px`,
      top: `${top}px`,
    };
  };

  if (!isSearchPage || !isSubscribed) return null;

  // Show ask dialog before starting tutorial
  if (showAskDialog) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[99] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="bg-background dark:bg-slate-900 rounded-2xl shadow-2xl border border-border dark:border-slate-700 p-6 w-full max-w-sm"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-[#2B7FFF] to-[#1A5FCC] rounded-2xl flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                Welcome to RaiseBeacon!
              </h3>
              <p className="text-muted-foreground dark:text-slate-400 text-sm">
                Would you like a quick tour of the investor search tool?
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={startTutorial}
                className="w-full py-3 bg-gradient-to-r from-[#2B7FFF] to-[#1A5FCC] hover:from-[#1A5FCC] hover:to-[#2B7FFF] text-white font-semibold rounded-xl shadow-lg shadow-[#2B7FFF]/25 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Zap className="w-5 h-5" />
                Start Tutorial
              </button>
              <button
                onClick={skipTutorial}
                className="w-full py-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-muted-foreground dark:text-slate-400 font-medium rounded-xl transition-colors"
              >
                Skip for Now
              </button>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Show restart button when tutorial is not active but has been seen
  if (!isActive && hasSeenTutorial) {
    return (
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={resetTutorial}
        className="fixed bottom-6 left-6 z-40 flex items-center gap-2 px-4 py-3 bg-background dark:bg-slate-800 shadow-lg border border-border dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-medium hover:bg-muted dark:hover:bg-slate-700 hover:shadow-xl transition-all"
        title="Restart Tutorial"
      >
        <div className="w-8 h-8 bg-gradient-to-br from-[#2B7FFF] to-[#1A5FCC] rounded-lg flex items-center justify-center">
          <BookOpen className="w-4 h-4 text-white" />
        </div>
        <span>How to use RaiseBeacon</span>
      </motion.button>
    );
  }

  if (!isActive) return null;

  const tooltipStyles = getTooltipStyles();
  const hasTarget = targetRect && step.position !== 'center';

  return (
    <AnimatePresence>
      {isActive && (
        <>
          {/* Backdrop - full dim for center modal, spotlight with cutout for targets */}
          {!hasTarget ? (
            /* Center modal - full dim */
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[99] pointer-events-none bg-black/50"
            />
          ) : (
            <>
              {/* Main backdrop with hole using box-shadow */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed z-[100] pointer-events-none"
                style={{
                  left: targetRect.left - 10,
                  top: targetRect.top - 10,
                  width: targetRect.width + 20,
                  height: targetRect.height + 20,
                  borderRadius: 12,
                  boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
                }}
              />
              {/* Highlight ring */}
              <motion.div
                layoutId="tutorial-ring"
                initial={false}
                animate={{
                  left: targetRect.left - 10,
                  top: targetRect.top - 10,
                  width: targetRect.width + 20,
                  height: targetRect.height + 20,
                }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="fixed z-[101] rounded-xl pointer-events-none border-[3px] border-[#2B7FFF]"
                style={{
                  boxShadow: '0 0 0 4px rgba(43, 127, 255, 0.3), 0 0 20px 4px rgba(43, 127, 255, 0.4)',
                }}
              />
            </>
          )}

          {/* Pulse effect */}
          {targetRect && step.position !== 'center' && (
            <motion.div
              initial={false}
              animate={{
                left: targetRect.left - 10,
                top: targetRect.top - 10,
                width: targetRect.width + 20,
                height: targetRect.height + 20,
              }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed z-[101] rounded-xl pointer-events-none"
            >
              <motion.div
                className="absolute inset-0 rounded-xl border-2 border-[#2B7FFF]"
                animate={{ scale: [1, 1.08, 1], opacity: [0.6, 0, 0.6] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
            </motion.div>
          )}

          {/* Tooltip */}
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed z-[102] w-80 bg-background rounded-2xl shadow-2xl border border-slate-100 p-5 pointer-events-auto"
            style={tooltipStyles}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                {step.icon && (
                  <div className="w-10 h-10 bg-muted rounded-xl flex items-center justify-center">
                    {step.icon}
                  </div>
                )}
                <h3 className="text-base font-bold text-slate-900">{step.title}</h3>
              </div>
              <button
                onClick={handleSkip}
                className="text-slate-400 hover:text-muted-foreground p-1 -mr-1 -mt-1 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Description */}
            <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
              {step.description}
            </p>

            {/* Progress */}
            <div className="flex items-center justify-center gap-1.5 mb-4">
              {tutorialSteps.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentStep(idx)}
                  className={`h-1.5 rounded-full transition-all ${
                    idx === currentStep ? 'w-5 bg-[#2B7FFF]' : 'w-1.5 bg-slate-200'
                  }`}
                />
              ))}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <button
                onClick={handlePrev}
                disabled={isFirstStep}
                className="px-2 py-1.5 text-sm text-muted-foreground hover:text-slate-700 disabled:opacity-30 flex items-center rounded-lg hover:bg-muted transition-colors"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </button>

              <div className="flex items-center gap-3">
                {!isLastStep && (
                  <button
                    onClick={handleSkip}
                    className="px-2 py-1.5 text-sm text-muted-foreground hover:text-slate-700 rounded-lg hover:bg-muted transition-colors"
                  >
                    Skip
                  </button>
                )}
                <button
                  onClick={handleNext}
                  className="px-4 py-2 bg-[#2B7FFF] hover:bg-[#1A5FCC] text-white text-sm font-medium rounded-lg flex items-center transition-colors"
                >
                  {isLastStep ? 'Get Started' : 'Next'}
                  {!isLastStep && <ChevronRight className="w-4 h-4 ml-1" />}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

